import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const EmailVerification = ({ sessionId, onVerificationComplete }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const handleSendCode = async () => {
    try {
      setSendingCode(true);
      setError(null);

      await axios.post(`${API_URL}/voting-auth/verify-request`, {
        sessionId,
      });

      setCodeSent(true);
      setSuccess('Verification code sent to your email!');
    } catch (err) {
      console.error('Error sending code:', err);
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(`${API_URL}/voting-auth/verify`, {
        sessionId,
        code,
      });

      if (response.data.success) {
        setSuccess('Verification successful! Redirecting...');
        setTimeout(() => {
          onVerificationComplete(sessionId, response.data.electionId);
        }, 1500);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Invalid verification code');
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body p-4">
        <div className="text-center mb-4">
          <i className="bi bi-shield-lock" style={{ fontSize: '3.75rem', color: '#667eea' }}></i>
          <h4 className="mt-3 mb-2">Email Verification Required</h4>
          <p className="text-muted mb-0">
            We detected unusual activity for this voting session. Please verify your email to continue.
          </p>
        </div>

      {!codeSent ? (
        <div className="text-center">
          <p className="mb-3">
            Click the button below to receive a verification code via email.
          </p>
          <button
            className="btn btn-primary btn-lg w-100"
            onClick={handleSendCode}
            disabled={sendingCode}
          >
            {sendingCode ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Sending...
              </>
            ) : (
              <>
                <i className="bi bi-envelope me-2"></i>
                Send Verification Code
              </>
            )}
          </button>
        </div>
      ) : (
        <form onSubmit={handleVerify}>
          <p className="text-center mb-3">
            Enter the 6-digit code sent to your email:
          </p>

          <div className="mb-3">
            <input
              type="text"
              className="form-control form-control-lg text-center"
              style={{ 
                fontSize: '24px', 
                letterSpacing: '8px',
                fontFamily: 'monospace'
              }}
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
                setError(null);
              }}
              placeholder="000000"
              maxLength="6"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg w-100 mb-2"
            disabled={loading || code.length !== 6}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              'Verify & Continue'
            )}
          </button>

          <button
            type="button"
            className="btn btn-link w-100"
            onClick={handleSendCode}
            disabled={sendingCode}
          >
            {sendingCode ? 'Sending...' : 'Resend Code'}
          </button>
        </form>
      )}

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success mt-3" role="alert">
          {success}
        </div>
      )}

      <div className="mt-4 p-3 bg-light rounded">
        <small className="text-muted">
          <strong>Why am I seeing this?</strong><br />
          We detected that you're accessing your voting link from a different location or device. 
          This is a security measure to protect your vote.
        </small>
      </div>
    </div>
    </div>
  );
};

export default EmailVerification;
